import heroImage from '../assets/heroCzupur.png';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../lib/theme';
import { Reveal, RevealHeading, RevealGroup, RevealItem } from '../components/Reveal';

// ---------- Motyw klasyczny (bez zmian) ----------
const ClassicHome = ({ t }) => (
  <div className="animate-fadeIn">
    <section className="my-16 md:my-24 flex flex-col md:flex-row items-center gap-10 md:gap-16">
      <div className="md:w-1/2 flex justify-center">
        <div className="w-96 h-96 rounded-full bg-brand-gray shadow-xl overflow-hidden">
          <img src={heroImage} alt={t('home.about.imageAlt')} className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="md:w-1/2">
        <RevealHeading as="h2" text={t('home.about.title')} className="text-3xl md:text-4xl mb-6 text-center md:text-left text-brand-accent" />
        <RevealGroup className="space-y-4 text-brand-text-light/80 leading-relaxed">
          <RevealItem as="p">{t('home.about.p1')}</RevealItem>
          <RevealItem as="p">{t('home.about.p2')}</RevealItem>
          <RevealItem as="p">{t('home.about.p3')}</RevealItem>
        </RevealGroup>
      </div>
    </section>
  </div>
);

// ---------- Motyw Imperial: kinowy hero + elegancka sekcja „o hodowli" ----------
const ImperialHome = ({ t }) => (
  <div className="animate-fadeIn">
    {/* HERO */}
    <section className="relative min-h-[78vh] sm:min-h-[88vh] flex items-end overflow-hidden">
      <img
        src={heroImage}
        alt={t('home.about.imageAlt')}
        className="imp-kenburns absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908] via-[#0a0908]/55 to-[#0a0908]/10" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0908]/60 via-transparent to-transparent" aria-hidden />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-5 sm:px-6 pb-16 sm:pb-20 md:pb-28">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: 'easeOut' }}>
          <p className="text-brand-vermilion uppercase tracking-[0.25em] sm:tracking-[0.35em] text-[0.65rem] sm:text-xs md:text-sm mb-3 sm:mb-4">{t('home.hero.kicker')}</p>
          <h1 className="imp-hero-title font-display text-5xl sm:text-7xl md:text-8xl lg:text-9xl text-brand-text-light leading-[0.95] tracking-wide">
            Etsuko
          </h1>
          <div className="h-px w-20 sm:w-24 bg-gradient-to-r from-brand-accent to-transparent my-5 sm:my-6" aria-hidden />
          <p className="font-display italic text-lg sm:text-xl md:text-3xl text-brand-text-light/85 max-w-xl">{t('home.hero.tagline')}</p>
          <Link
            to="/nasze-psy"
            className="inline-flex items-center gap-3 mt-10 px-7 py-3 border border-brand-accent/70 text-brand-accent uppercase tracking-[0.25em] text-xs hover:bg-brand-accent hover:text-brand-bg-center transition-colors duration-300"
          >
            {t('home.hero.cta')} <span aria-hidden>→</span>
          </Link>
        </motion.div>
      </div>

      <div className="imp-seal absolute right-6 md:right-12 bottom-24 md:bottom-32 text-2xl md:text-3xl hidden sm:block" aria-hidden>悦子</div>
      <div className="imp-scroll-hint absolute left-1/2 bottom-6 text-brand-text-light/60 text-[10px] uppercase tracking-[0.3em]">{t('home.hero.scroll')}</div>
    </section>

    {/* O HODOWLI */}
    <section className="max-w-6xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
      <motion.div
        className="md:col-span-5 relative"
        initial={{ opacity: 0, x: -24 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute -inset-3 border border-brand-accent/40 translate-x-3 translate-y-3" aria-hidden />
        <img src={heroImage} alt={t('home.about.imageAlt')} className="relative w-full aspect-[4/5] object-cover" loading="lazy" decoding="async" />
      </motion.div>
      <motion.div
        className="md:col-span-7"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, delay: 0.15 }}
      >
        <Reveal as="p" y={14} className="text-brand-vermilion uppercase tracking-[0.3em] text-xs mb-3">{t('home.hero.kicker')}</Reveal>
        <RevealHeading as="h2" text={t('home.about.title')} delay={0.1} className="font-display text-4xl md:text-5xl text-brand-accent mb-8" />
        <RevealGroup className="space-y-5 text-brand-text-light/85 text-lg leading-relaxed" delay={0.15}>
          <RevealItem as="p" className="first-letter:font-display first-letter:text-5xl first-letter:text-brand-accent first-letter:float-left first-letter:mr-3 first-letter:leading-none">
            {t('home.about.p1')}
          </RevealItem>
          <RevealItem as="p">{t('home.about.p2')}</RevealItem>
          <RevealItem as="p">{t('home.about.p3')}</RevealItem>
        </RevealGroup>
      </motion.div>
    </section>
  </div>
);

const HomePage = () => {
  const { t } = useTranslation();
  const { isImperial } = useTheme();
  return isImperial ? <ImperialHome t={t} /> : <ClassicHome t={t} />;
};

export default HomePage;
